import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import Aspera from "csi/comm/Aspera";
import html from "csi/html";
import { deleteFileData, getFileData, validateFiles } from "csi/service/fileService";
import { openLightbox } from "csi/service/lightboxService";
import { CommentDetail } from "csi/state/comments";
import { useStore } from "csi/state/hooks";
import { IFileData, cnl } from "csi/typings";
import UserAvatar from "csi/user/UserAvatar";
import IconButton from "designSystem/components/button/IconButton";
import DeleteDialog from "designSystem/components/dialog/DeleteDialog";
import ErrorDialog from "designSystem/components/dialog/ErrorDialog";
import File from "designSystem/components/file/File";
import FilesPopover from "designSystem/components/file/FilesPopover";
import FlexRowCenter from "designSystem/components/flex/FlexRowCenter";
import GridItemGrow from "designSystem/components/grid/GridItemGrow";
import Text from "designSystem/components/text/Text";
import { Button } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent, useCallback, useState } from "react";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import CommentAuthorAndDate from "./CommentAuthorAndDate";
import CommentForm from "./CommentForm";
import CommentsRevisionChip from "./CommentsRevisionChip";
import InvalidAttachmentDialog from "./InvalidAttachmentDialog";

interface CommentCardProps {
	/** comment url */
	commentUrl: cnl;
}

const CommentCard: FunctionComponent<CommentCardProps> = observer(({ commentUrl }) => {
	const loc = useLocale();
	const { commentsStore, rightPaneStore } = useStore();
	const {
		attachFiles,
		cache,
		closeErrorDialog,
		deleteAttachment,
		deleteComment,
		editComment,
		finishInitialization,
		getCommentDetail,
		isFileUploadInProgress,
		selectedCommentResolved,
		startReplySpecificComment,
	} = commentsStore;

	const commentDetail = getCommentDetail(commentUrl) || {} as CommentDetail;
	const { attachments, author, content, editable, editing, pinName, revisionCount, threeDInfo } = commentDetail;
	const numAttachments = attachments?.length;
	const viewAll = numAttachments >= 4;

	const [showAttachments, setShowAttachments] = useState(viewAll && isFileUploadInProgress);
	const [attachmentsAnchorEl, setAttachmentsAnchorEl] = useState<HTMLButtonElement | null>(null);

	const viewAllRef = useCallback((domNode) => {
		setAttachmentsAnchorEl(domNode);
	}, [editing, viewAll]);

	const onDownload = (fileData: IFileData): void => {
		Aspera.downloadFile(fileData.id);
	};

	const onAttach = async () => {
		if (Aspera.isEnabled()) {
			Aspera.uploadFile(commentUrl, "Files", true, true, null);
			return;
		}
		const files = await html.selectFiles();

		try {
			await validateFiles(files);

			const fileData = files.map(getFileData);

			try {
				// publish attachments
				await attachFiles(commentUrl, fileData);
			} catch (error) {
				// error while publishing files
				rightPaneStore.openDialog(ErrorDialog, {
					messageRes: "FileBrowse_Error",
					onClose: closeErrorDialog,
					titleRes: "CommentAttach",
				});
			} finally {
				// cleanup
				fileData.forEach(deleteFileData);
			}
		} catch (filenames) {
			// invalid files
			rightPaneStore.openDialog(InvalidAttachmentDialog, { filenames });
		}
	};

	const onClickImage = (fileData: IFileData) => {
		const imageNode = cache.get(fileData.imageUrl);
		onCloseAttachments();
		openLightbox(imageNode, cache, true);
	};

	const onCloseAttachments = () => {
		setShowAttachments(false);
	};

	const onDelete = () => {
		rightPaneStore.openDialog(DeleteDialog, {
			typeName: loc.resource("Conversation"),
			onConfirm: async () => {
				try {
					await deleteComment(commentUrl);
				} catch (error) {
					rightPaneStore.openDialog(ErrorDialog, {
						onClose: closeErrorDialog,
					});
				}
			},
		});
	};

	const onDeleteAttachment = async (fileData: IFileData) => {
		try {
			await deleteAttachment(commentUrl, fileData);
		} catch (error) {
			rightPaneStore.openDialog(ErrorDialog, {
				onClose: closeErrorDialog,
			});
		}
	};

	return (
		<Fragment>
			{finishInitialization && (
				<Grid
					container
					spacing={1}
					wrap="nowrap"
				>
					<Grid item>
						<Box pl={3} py={3}>
							<UserAvatar user={author} />
						</Box>
					</Grid>
					<GridItemGrow>
						<Box pr={3} py={3}>
							<FlexRowCenter height={24}>
								<CommentAuthorAndDate commentDetail={commentDetail} renderAsLink />
							</FlexRowCenter>
							{!editing && (
								<Fragment>
									<Box mt={2}>
										<FroalaEditorView model={content} />
									</Box>
									<Box mt={2}>
										<Grid
											container
											direction="column"
											spacing={1}
											wrap="nowrap"
										>
											{viewAll ? (
												<Grid item>
													<FlexRowCenter>
														<Icon className="far fa-file" />
														<Text
															grow
															res="CommentsAttachments"
															resParams={[numAttachments]}
															style={{ marginLeft: 8 }}
														/>
														<Link
															onClick={() => setShowAttachments(true)}
															ref={viewAllRef}
															style={{ padding: "2px 0" }}
														>
															<Text res="ViewAll" />
														</Link>
														<FilesPopover
															anchorEl={attachmentsAnchorEl}
															files={attachments}
															onClose={onCloseAttachments}
															onDelete={editable ? onDeleteAttachment : null}
															onImage={onClickImage}
															open={showAttachments}
														/>
													</FlexRowCenter>
												</Grid>
											)
												:
												attachments.map((fileData, i) => {
													const { file, id } = fileData;
													const inProgress = !!file;
													return (
														<Grid item key={id}>
															<File
																download={!inProgress}
																fileData={fileData}
																onDelete={editable && !inProgress ? () => onDeleteAttachment(fileData) : null}
																onDownload={Aspera.isEnabled() ? () => onDownload(fileData) : null}
																onImage={() => onClickImage(fileData)}
																uploading={inProgress}
															/>
														</Grid>
													);
												})
											}
										</Grid>
									</Box>
									<CommentsRevisionChip
										pinName={pinName}
										revisionCount={revisionCount}
										threeDInfo={threeDInfo}
									/>
									<Box mt={2}>
										<Grid
											alignItems="center"
											container
											justifyContent="flex-end"
											spacing={1}
										>
											{!selectedCommentResolved && (
												<Grid item>
													<Button
														design="link"
														disabled={author.$Type == "Node"}
														onClick={() => startReplySpecificComment(author)}
														res="CommentReply"
													/>
												</Grid>
											)}												
											{editable && (
												<>
													<Grid item>
														<IconButton
															onClick={() => editComment(commentUrl)}
															res="Edit"
															size="small"
														>
															<EditIcon />
														</IconButton>
													</Grid>
													<Grid item>
														<IconButton
															onClick={onDelete}
															res="Delete"
															size="small"
														>
															<DeleteForeverIcon />
														</IconButton>
													</Grid>
													<Grid item>
														<IconButton
															onClick={onAttach}
															res="CommentAttach"
															size="small"
														>
															<AttachFileIcon />
														</IconButton>
													</Grid>
												</>
											)}
										</Grid>
									</Box>
								</Fragment>
							)}
						</Box>
					</GridItemGrow>
				</Grid>
			)}
			{editing && finishInitialization && (
				<Box mt={2} mx={3}>
					<CommentForm detail={commentDetail} />
				</Box>
			)}
		</Fragment>
	);
});

export default CommentCard;